import type { ConversationItemProps } from './index.types';
import { MobXProviderContext, observer } from 'mobx-react';
import { useIsPersonInactive } from 'Modules/conversation/hooks/useIsPersonInactive';
import { useUIStore } from 'Modules/ui/index.store';
import * as React from 'react';
import { useNavigate } from 'react-router';
import { RootStoreProps } from 'Stores/RootStore.types';
import { getCurrentConversationId } from 'Utils/getCurrentConversationId';
import { cn } from '~/lib/utils';
import ConversationItemAvatar from './ConversationItemAvatar';
import ConversationItemHoverIcons from './ConversationItemHoverIcons';
import ConversationItemIcons from './ConversationItemIcons';
import ConversationItemLabel from './ConversationItemLabel';
import { useOpenChatDetails } from './hooks/useOpenChatDetails';

const ConversationItem: React.FC<ConversationItemProps> = ({
  conversation,
}) => {
  const { ui: uiStore, conversation: conversationStore } =
    React.useContext<RootStoreProps>(MobXProviderContext);

  const navigate = useNavigate();
  const openChatDetails = useOpenChatDetails(conversation);

  const isPersonInactive = useIsPersonInactive(conversation);

  const currentConversationId = getCurrentConversationId();

  const [isHovered, setIsHovered] = React.useState(false);
  const [isMouseOverPopoverContent, setIsMouseOverPopoverContent] =
    React.useState(false);

  const isMoreOptionsOpen =
    uiStore.showMoreActionOptions.conversationId === conversation?.id &&
    uiStore.showMoreActionOptions.show;

  const isPopoverOpenButNotHovering =
    isMoreOptionsOpen && (isMouseOverPopoverContent || !isHovered);

  const shouldHideIconsOnHover =
    !isMoreOptionsOpen || (isHovered && !isMouseOverPopoverContent);

  const shouldHideIcons =
    isMoreOptionsOpen && isHovered && !isMouseOverPopoverContent;

  React.useEffect(() => {
    if (!isMoreOptionsOpen) setIsMouseOverPopoverContent(false);
  }, [isMoreOptionsOpen]);

  const openConversation = React.useCallback(() => {
    if (isPersonInactive) {
      useUIStore.getState().resetEntitySidePanel();
    } else if (useUIStore.getState().entitySidePanelInfo) {
      openChatDetails();
    }

    uiStore.setMessageIdToScroll('');

    conversationStore.setPreviousConversation(currentConversationId);

    void navigate(
      conversation?.id ? `/chat/conversations/${conversation.id}/chat` : '/chat'
    );

    uiStore.setMessageIdToScroll('');
  }, [
    conversation.id,
    conversationStore,
    currentConversationId,
    isPersonInactive,
    navigate,
    openChatDetails,
    uiStore,
  ]);

  return (
    <div
      id={conversation?.id}
      onClick={openConversation}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      className={cn(
        'group flex items-center h-[56px] px-[10px] border-transparent border-l-[3px] cursor-pointer hover:bg-accent hover:border-primary',
        {
          'bg-accent border-primary':
            currentConversationId === conversation?.id,
        }
      )}
      data-testid={`conversation-${conversation?.id}`}
    >
      <ConversationItemAvatar conversation={conversation} />

      <div className={cn('grow ml-[14px] overflow-hidden')}>
        <ConversationItemLabel
          conversation={conversation}
          isConversationSelect={currentConversationId === conversation?.id}
        />
      </div>

      <div className={cn('relative flex items-center shrink-0 ml-[10px]')}>
        <ConversationItemIcons
          conversation={conversation}
          className={cn(
            {
              'group-hover:hidden': shouldHideIconsOnHover,
              hidden: shouldHideIcons,
            },
            isPopoverOpenButNotHovering && 'absolute right-0'
          )}
        />
        <ConversationItemHoverIcons
          conversation={conversation}
          className={cn(
            'hidden group-hover:flex',
            isMoreOptionsOpen && 'flex',
            isPopoverOpenButNotHovering && 'opacity-0',
          )}
          onPopoverContentHoverChange={setIsMouseOverPopoverContent}
        />
      </div>
    </div>
  );
};

export default observer(ConversationItem);
